import { Dayjs } from 'dayjs';
import { ReportEntity, ReportStatus, UserRole } from '../../services/api.types';
import { formatHour, formatMoney } from '../../utils/formatters';
import clsx from 'clsx';
import { IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAppSelector } from '../../hooks';

const ReportsSummaryField = ({
  title,
  value,
}: {
  title: string;
  value: string | number;
}) => (
  <div className="flex flex-col p-0 mb-2">
    <p className="mt-2 mb-0">{title}:</p>
    <b className="text-left">{value}</b>
  </div>
);

export type ReportsSummaryProps = {
  selectedUserRole?: UserRole;
  reports: ReportEntity[];
  filterMonth: Dayjs | null;
  reportsCategory: string;
  className?: string;
  onClose?: () => void;
};

export const ReportsSummary = ({
  selectedUserRole,
  reports,
  filterMonth,
  reportsCategory,
  className,
  onClose,
}: ReportsSummaryProps) => {
  const user = useAppSelector((state) => state.user);
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const isTeacher = user?.role === UserRole.TEACHER;

  const calcEventValue = (hourClassValue: number, duration: number) => {
    return hourClassValue * (duration / 60);
  };

  const showTeacherPrivateFields =
    (isAdminOrEmployee && selectedUserRole === UserRole.TEACHER) || isTeacher;
  const showStudentPrivateFields =
    isAdminOrEmployee &&
    (selectedUserRole === UserRole.STUDENT ||
      selectedUserRole === UserRole.RESPONSIBLE);

  const pendingReports = reports.filter(
    (report) => report.status === ReportStatus.PENDING
  ).length;
  const doneReports = reports.filter(
    (report) => report.status === ReportStatus.DONE
  ).length;

  const classToTeacherTotalValue = reports.reduce(
    (total, event) =>
      total + calcEventValue(event.valueToTeacher, event.duration),
    0
  );
  const classFromResponsibleTotalValue = reports.reduce(
    (total, event) =>
      total + calcEventValue(event.valueFromResponsible, event.duration),
    0
  );
  const tripTotalValue = reports.reduce(
    (total, event) => total + event.valueToTrip,
    0
  );

  const classHoursDuration = reports.reduce(
    (total, event) => total + event.duration,
    0
  );
  const classGivenHours = formatHour(classHoursDuration);

  return (
    <div
      className={clsx(
        'w-[350px] h-min',
        'flex flex-col bg-orange-light',
        'rounded-[45px] p-4 px-6 my-0.5',
        className
      )}
    >
      <div
        className={clsx(
          'w-full mb-3 text-center bg-white rounded-[45px]',
          'grid grid-cols-1-auto-1 items-center'
        )}
      >
        <div></div>
        <h3 className="px-0">{filterMonth?.format('MMMM [de] YYYY')}</h3>
        <div>
          {onClose && (
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          )}
        </div>
      </div>

      <div className="flex flex-col my-0 text-base gap-0 font-normal">
        <ReportsSummaryField
          title="Quantidade de horas dadas"
          value={classGivenHours}
        />

        {showTeacherPrivateFields && (
          <ReportsSummaryField
            title="Valor referente às horas dadas"
            value={formatMoney(classToTeacherTotalValue)}
          />
        )}

        {showStudentPrivateFields && (
          <ReportsSummaryField
            title="Valor referente às horas dadas"
            value={formatMoney(classFromResponsibleTotalValue)}
          />
        )}

        <ReportsSummaryField
          title="Valor referente aos deslocamentos pelas aulas dadas"
          value={formatMoney(tripTotalValue)}
        />

        {showTeacherPrivateFields && (
          <ReportsSummaryField
            title="Valor total a receber pelas aulas dadas"
            value={formatMoney(classToTeacherTotalValue + tripTotalValue)}
          />
        )}

        {showStudentPrivateFields && (
          <ReportsSummaryField
            title="Valor total a pagar pelas aulas dadas"
            value={formatMoney(classFromResponsibleTotalValue + tripTotalValue)}
          />
        )}

        {reportsCategory !== ReportStatus.DONE && (
          <ReportsSummaryField
            title="Quantidade de relatórios pendentes"
            value={pendingReports}
          />
        )}
        {reportsCategory !== ReportStatus.PENDING && (
          <ReportsSummaryField
            title="Quantidade de relatórios feitos"
            value={doneReports}
          />
        )}
      </div>
    </div>
  );
};
